/**
 * WHO-6309. Transport seam for the seller invoice and VAT fields.
 *
 * WHY NOT APOLLO
 * Same reason as `src/features/pro-buyer-price/data/pro-buyer-price-client.ts`
 * (WHO-5743): codegen validates every `.graphql` document against the gateway
 * schema, and `Product.vatRegime`, `Product.priceExclVat`, the cart item
 * fields, the order invoice fields, `Merchant.defaultVatRegime` and
 * `acceptInvoiceMandate` are being built in a parallel gateway PR. One
 * document naming them would fail codegen and remove every Vercel preview.
 *
 * So the operations are plain strings sent through `dashboardRequest`, and
 * every reader is fail-soft: a gateway without the fields, an error or a
 * timeout comes back as "no data", and nothing new renders.
 *
 * FOLLOW-UP: once the gateway fields are on production, move these to
 * `.graphql` documents (fragments on the product, cart, order and merchant
 * documents) with Apollo hooks, import `VatRegime` and `SellerInvoiceMode`
 * from `@/Apollo/schema`, and delete this file.
 */
import { GRAPHQL_ENDPOINT } from "@/config";
import { dashboardRequest } from "@/features/account-dashboard/data/dashboard-client";
import {
  SELLER_INVOICE_MODE,
  SellerInvoiceMode,
  VAT_REGIME,
  VatRegime,
} from "@/features/seller-invoice/static/seller-invoice.static";
import {
  SellerInvoiceDocFields,
  SellerInvoiceMoney,
  SellerInvoiceRoute,
  buildSellerInvoiceRouteUrl,
} from "@/features/seller-invoice/utils/seller-invoice.utils";

export type ProductVat = {
  vatRegime: VatRegime | null;
  priceExclVat: SellerInvoiceMoney | null;
};

export type MerchantInvoiceSettings = {
  merchantId: string;
  defaultVatRegime: VatRegime | null;
  invoiceMandateAcceptedAt: string | null;
};

export const PRODUCT_VAT_BATCH_SIZE = 24;

/* -- parsing --------------------------------------------------------------- */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const asString = (value: unknown) =>
  typeof value === "string" && value !== "" ? value : null;

export const parseMoney = (value: unknown): SellerInvoiceMoney | null =>
  isRecord(value) &&
  typeof value.amount === "number" &&
  typeof value.currency === "string"
    ? { amount: value.amount, currency: value.currency }
    : null;

export const parseVatRegime = (value: unknown): VatRegime | null => {
  const upper = typeof value === "string" ? value.toUpperCase() : null;

  return (Object.values(VAT_REGIME) as string[]).includes(upper ?? "")
    ? (upper as VatRegime)
    : null;
};

const parseMode = (value: unknown): SellerInvoiceMode | null => {
  const upper = typeof value === "string" ? value.toUpperCase() : null;

  return (Object.values(SELLER_INVOICE_MODE) as string[]).includes(upper ?? "")
    ? (upper as SellerInvoiceMode)
    : null;
};

export const parseProductVat = (value: unknown): ProductVat | null =>
  isRecord(value)
    ? {
        vatRegime: parseVatRegime(value.vatRegime),
        priceExclVat: parseMoney(value.priceExclVat),
      }
    : null;

export const parseOrderInvoiceFields = (
  value: unknown
): SellerInvoiceDocFields | null => {
  if (!isRecord(value) || typeof value.sellerInvoiceAvailable !== "boolean") {
    return null;
  }

  const upload = isRecord(value.sellerInvoiceUpload)
    ? value.sellerInvoiceUpload
    : null;
  const fileName = upload ? asString(upload.fileName) : null;

  return {
    sellerInvoiceAvailable: value.sellerInvoiceAvailable,
    sellerInvoiceNumber: asString(value.sellerInvoiceNumber),
    whoppahInvoiceAvailable: value.whoppahInvoiceAvailable === true,
    sellerInvoiceUpload:
      upload && fileName
        ? { fileName, uploadedAt: asString(upload.uploadedAt) }
        : null,
    invoiceUploadAllowed: value.invoiceUploadAllowed === true,
    sellerInvoiceMode: parseMode(value.sellerInvoiceMode),
    salesConfirmationAvailable: value.salesConfirmationAvailable === true,
    commissionInvoiceAvailable: value.commissionInvoiceAvailable === true,
  };
};

/* -- operations ------------------------------------------------------------ */

const MONEY = "{ amount currency }";

const aliasedOperation = (
  name: string,
  count: number,
  field: (index: number) => string
) => {
  const variables = Array.from(
    { length: count },
    (_unused, index) => `$id${index}: UUID!`
  ).join(", ");
  const fields = Array.from({ length: count }, (_unused, index) =>
    field(index)
  ).join("");

  return `query ${name}(${variables}) {${fields} }`;
};

export const productVatOperation = (count: number) =>
  aliasedOperation(
    "SellerInvoiceProductVat",
    count,
    index =>
      ` p${index}: product(id: $id${index}) { id vatRegime priceExclVat ${MONEY} }`
  );

export const CART_VAT_OPERATION = `query SellerInvoiceCartVat { cart { items { id vatRegime priceExclVat ${MONEY} } } }`;

const ORDER_INVOICE_SELECTION =
  "id sellerInvoiceAvailable sellerInvoiceNumber whoppahInvoiceAvailable" +
  " sellerInvoiceUpload { fileName uploadedAt } invoiceUploadAllowed sellerInvoiceMode" +
  " salesConfirmationAvailable commissionInvoiceAvailable";

export const ORDER_INVOICE_OPERATION = `query SellerInvoiceOrder($id: UUID!) { order(id: $id) { ${ORDER_INVOICE_SELECTION} } }`;

export const MERCHANT_VAT_REGIME_OPERATION =
  "query SellerInvoiceMerchantVat { me { merchants { id defaultVatRegime } } }";

/** Not named in the contract; read on its own so its absence costs nothing else. */
export const MERCHANT_MANDATE_OPERATION =
  "query SellerInvoiceMandate { me { merchants { id invoiceMandateAcceptedAt } } }";

/** Existing fields only: whether the viewer's merchant is a business (D7). */
export const VIEWER_MERCHANT_TYPE_OPERATION =
  "query SellerInvoiceViewerType { me { merchants { id type } } }";

export const ACCEPT_INVOICE_MANDATE_OPERATION =
  "mutation SellerInvoiceAcceptMandate($version: String!) { acceptInvoiceMandate(version: $version) }";

const idVariables = (ids: readonly string[]) =>
  Object.fromEntries(ids.map((id, index) => [`id${index}`, id]));

/* -- readers (fail-soft) --------------------------------------------------- */

export const fetchProductVat = async (
  token: string | null,
  ids: readonly string[]
): Promise<Map<string, ProductVat>> => {
  const result = new Map<string, ProductVat>();

  if (ids.length === 0) {
    return result;
  }

  const response = await dashboardRequest<Record<string, unknown>>({
    operation: productVatOperation(ids.length),
    variables: idVariables(ids),
    token,
  });

  if (!response.ok) {
    return result;
  }

  ids.forEach((id, index) => {
    const vat = parseProductVat(response.data[`p${index}`]);

    if (vat) {
      result.set(id, vat);
    }
  });

  return result;
};

export const fetchCartVat = async (
  token: string | null
): Promise<Map<string, ProductVat> | null> => {
  const response = await dashboardRequest<{ cart?: { items?: unknown } }>({
    operation: CART_VAT_OPERATION,
    token,
  });

  if (!response.ok || !Array.isArray(response.data.cart?.items)) {
    return null;
  }

  const lines = new Map<string, ProductVat>();

  for (const item of response.data.cart.items as unknown[]) {
    const vat = parseProductVat(item);

    if (vat && isRecord(item) && typeof item.id === "string") {
      lines.set(item.id, vat);
    }
  }

  return lines;
};

export const fetchOrderInvoiceFields = async (
  token: string | null,
  orderId: string
): Promise<SellerInvoiceDocFields | null> => {
  const response = await dashboardRequest<{ order?: unknown }>({
    operation: ORDER_INVOICE_OPERATION,
    variables: { id: orderId },
    token,
  });

  return response.ok ? parseOrderInvoiceFields(response.data.order) : null;
};

const firstMerchant = (data: unknown) => {
  const merchants =
    isRecord(data) && isRecord(data.me) ? data.me.merchants : null;
  const merchant = Array.isArray(merchants) ? merchants[0] : null;

  return isRecord(merchant) && typeof merchant.id === "string"
    ? merchant
    : null;
};

export const fetchViewerIsBusiness = async (
  token: string | null
): Promise<boolean> => {
  const response = await dashboardRequest<unknown>({
    operation: VIEWER_MERCHANT_TYPE_OPERATION,
    token,
  });
  const merchant = response.ok ? firstMerchant(response.data) : null;

  return (
    typeof merchant?.type === "string" &&
    merchant.type.toUpperCase() === "BUSINESS"
  );
};

export const fetchMerchantInvoiceSettings = async (
  token: string | null
): Promise<MerchantInvoiceSettings | null> => {
  const [regime, mandate] = await Promise.all([
    dashboardRequest<unknown>({
      operation: MERCHANT_VAT_REGIME_OPERATION,
      token,
    }),
    dashboardRequest<unknown>({ operation: MERCHANT_MANDATE_OPERATION, token }),
  ]);
  const regimeMerchant = regime.ok ? firstMerchant(regime.data) : null;
  const mandateMerchant = mandate.ok ? firstMerchant(mandate.data) : null;
  const merchantId = (regimeMerchant?.id ?? mandateMerchant?.id) as
    | string
    | undefined;

  if (!merchantId) {
    return null;
  }

  return {
    merchantId,
    defaultVatRegime: parseVatRegime(regimeMerchant?.defaultVatRegime),
    invoiceMandateAcceptedAt: asString(
      mandateMerchant?.invoiceMandateAcceptedAt
    ),
  };
};

export const acceptInvoiceMandate = async (
  token: string | null,
  version: string
): Promise<boolean> => {
  const response = await dashboardRequest<unknown>({
    operation: ACCEPT_INVOICE_MANDATE_OPERATION,
    variables: { version },
    token,
  });

  return response.ok;
};

/* -- REST routes ----------------------------------------------------------- */

export const sellerInvoiceRouteUrl = (
  route: SellerInvoiceRoute,
  orderPk: string
) => buildSellerInvoiceRouteUrl(GRAPHQL_ENDPOINT, route, orderPk);

/** GET a PDF with the bearer token, exactly like the existing invoice download. */
export const downloadInvoicePdf = async (
  token: string | null,
  route: SellerInvoiceRoute,
  orderPk: string
): Promise<Blob> => {
  const response = await window.fetch(sellerInvoiceRouteUrl(route, orderPk), {
    headers: new Headers({ Authorization: `Bearer ${token}` }),
  });

  if (!response.ok) {
    throw new Error(`Invoice download failed: ${response.status}`);
  }

  return response.blob();
};

/** POST (first upload) or PUT (replace) the seller's PDF. */
export const uploadSellerInvoice = async (
  token: string | null,
  orderPk: string,
  file: File,
  replace: boolean
): Promise<boolean> => {
  const body = new FormData();
  body.append("file", file, file.name);

  const response = await window.fetch(
    sellerInvoiceRouteUrl("seller-invoice-upload", orderPk),
    {
      method: replace ? "PUT" : "POST",
      headers: new Headers({ Authorization: `Bearer ${token}` }),
      body,
    }
  );

  return response.ok;
};
